import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "@/components/CookieConsentContext";

const PROJECT_KEY = "phc_DbsPb39SRc8z3EiQ6Dhj6ikv4H4rTKcht9d4sZSesceP";
const API_HOST = "https://ap.emergent.sh";

function initializePostHog() {
  if (window.posthog?.__SV) return;

  (function (document, posthog) {
    let methods;
    let index;
    let instance;
    let script;

    window.posthog = posthog;
    posthog._i = [];
    posthog.init = function (key, options, name) {
      function queueMethod(target, method) {
        const parts = method.split(".");
        if (parts.length === 2) {
          target = target[parts[0]];
          method = parts[1];
        }
        target[method] = function () {
          target.push([method, ...arguments]);
        };
      }

      script = document.createElement("script");
      script.type = "text/javascript";
      script.crossOrigin = "anonymous";
      script.async = true;
      script.src = options.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js";
      document.head.appendChild(script);

      instance = posthog;
      if (name) instance = posthog[name] = [];
      instance.people = instance.people || [];
      instance.toString = function (stub) {
        return `posthog${name ? `.${name}` : ""}${stub ? " (stub)" : ""}`;
      };
      instance.people.toString = () => `${instance.toString(1)}.people (stub)`;

      methods = "init capture identify alias setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags group resetGroups register register_once unregister getFeatureFlag getFeatureFlagPayload isFeatureEnabled opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing startSessionRecording stopSessionRecording sessionRecordingStarted get_distinct_id getGroups get_session_id get_session_replay_url".split(" ");
      for (index = 0; index < methods.length; index += 1) queueMethod(instance, methods[index]);
      posthog._i.push([key, options, name]);
    };
    posthog.__SV = 1;
  })(document, window.posthog || []);

  const loader = document.createElement("script");
  loader.type = "text/javascript";
  loader.crossOrigin = "anonymous";
  loader.async = true;
  loader.src = API_HOST.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js";
  document.head.appendChild(loader);
}

function clearPostHogStorage() {
  [window.localStorage, window.sessionStorage].forEach((storage) => {
    try {
      for (let index = storage.length - 1; index >= 0; index -= 1) {
        const key = storage.key(index);
        if (key?.startsWith("ph_") || key?.startsWith("__ph_opt_in_out_") || key === "lastExternalReferrer" || key === "lastExternalReferrerTime") {
          storage.removeItem(key);
        }
      }
    } catch {
      // Storage may be disabled by the browser.
    }
  });

  document.cookie.split(";").forEach((entry) => {
    const name = entry.split("=")[0].trim();
    if (name.startsWith("ph_")) {
      const host = window.location.hostname;
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${host}; SameSite=Lax`;
      if (host.includes(".")) document.cookie = `${name}=; Max-Age=0; path=/; domain=.${host}; SameSite=Lax`;
    }
  });
}

function safePath(pathname) {
  return pathname.replace(/^\/pack\/planificador\/[^/]+/, "/pack/planificador/:id");
}

function PostHogAnalytics() {
  const location = useLocation();
  const { consent } = useCookieConsent();
  const lastPageView = useRef(null);

  useEffect(() => {
    if (!consent.analytics) {
      if (window.__endosmundosPostHogInitialized && window.posthog) {
        window.posthog.opt_out_capturing?.({ clear_persistence: true });
        window.posthog.stopSessionRecording?.();
      }
      clearPostHogStorage();
      lastPageView.current = null;
      return;
    }

    if (!window.__endosmundosPostHogInitialized) {
      initializePostHog();
      window.posthog.init(PROJECT_KEY, {
        api_host: API_HOST,
        person_profiles: "identified_only",
        capture_pageview: false,
        autocapture: false,
        disable_session_recording: true,
        capture_performance: false,
      });
      window.__endosmundosPostHogInitialized = true;
    } else {
      window.posthog.opt_in_capturing?.();
    }
  }, [consent.analytics]);

  useEffect(() => {
    if (!consent.analytics || !window.__endosmundosPostHogInitialized || !window.posthog?.capture) return;

    const pathname = safePath(location.pathname);
    if (lastPageView.current === pathname) return;

    window.posthog.capture("$pageview", {
      $current_url: `${window.location.origin}${pathname}`,
      $pathname: pathname,
      $referrer: window.location.origin,
    });
    lastPageView.current = pathname;
  }, [consent.analytics, location.pathname]);

  return null;
}

export default PostHogAnalytics;